import { useEffect, useState } from "react";

import { useProfile, useUpdateProfile } from "../api";
import { readApiErrorMessage } from "../errors";

// Phase 4a customer-portal FR-9 (Contact tab). Email shown as a
// verified badge but read-only (email-change flow is out of v1).
// Comms preferences live on `preferred_notification_channel`.
//
// Phase 4u portal-profile-polish — design grade
// `.notes/design-grades/portal/03-profile.md` called out three things:
// the black Save button was inconsistent with the brand-red CTA system,
// inputs were plain `rounded border` (no focus state from the brand),
// and the Phone / Notify-me fields lacked the helper copy that builds
// trust around what JDR does with the contact info.
export function ContactTab(): JSX.Element {
  const q = useProfile();
  const save = useUpdateProfile();

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!q.data || dirty) return;
    setFirst(q.data.first_name ?? "");
    setLast(q.data.last_name ?? "");
    setPhone(q.data.phone ?? "");
    setCompany(q.data.company_name ?? "");
    setChannel(q.data.preferred_notification_channel === "sms" ? "sms" : "email");
  }, [q.data, dirty]);

  function mark<T>(setter: (v: T) => void) {
    return (v: T) => {
      setDirty(true);
      setter(v);
    };
  }

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-brand-red">Couldn&rsquo;t load profile.</div>;

  function handleSave(): void {
    setSaveError(null);
    save.mutate(
      {
        first_name: first,
        last_name: last,
        phone,
        company_name: company,
        preferred_notification_channel: channel
      },
      {
        onSuccess: () => setDirty(false),
        onError: (e) => setSaveError(readApiErrorMessage(e))
      }
    );
  }

  return (
    <div className="space-y-4">
      <Field label="Email" hint="Receipts and rental agreements are sent here.">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-brand-black">{q.data.email}</span>
          {q.data.email_confirmed ? (
            <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
              Verified
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              Unverified
            </span>
          )}
        </div>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name">
          <Input value={first} onChange={mark(setFirst)} testid="profile-first" autoComplete="given-name" />
        </Field>
        <Field label="Last name">
          <Input value={last} onChange={mark(setLast)} testid="profile-last" autoComplete="family-name" />
        </Field>
      </div>

      <Field
        label="Phone"
        hint="We'll text delivery windows and pickup-ready reminders to this number."
      >
        <Input
          value={phone}
          onChange={mark(setPhone)}
          testid="profile-phone"
          type="tel"
          autoComplete="tel"
        />
      </Field>

      <Field label="Company (optional)" hint="Shown on your rental agreement.">
        <Input value={company} onChange={mark(setCompany)} testid="profile-company" autoComplete="organization" />
      </Field>

      <Field
        label="Notify me by"
        hint="Email is the default while we finish carrier on-boarding for SMS reminders."
      >
        <select
          value={channel}
          onChange={(e) => mark(setChannel)(e.target.value as "email" | "sms")}
          className="block min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-brand-black shadow-sm transition focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy disabled:opacity-50"
          data-testid="profile-channel"
        >
          <option value="email">Email</option>
          <option value="sms">SMS (coming soon)</option>
        </select>
      </Field>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={save.isPending || !dirty}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-brand-red px-5 py-2 text-sm font-semibold text-brand-white shadow-sm transition hover:bg-brand-red-dark focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="profile-save"
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {save.isSuccess && !dirty ? (
          <span className="text-xs font-medium text-green-700" aria-live="polite">
            Saved
          </span>
        ) : null}
      </div>

      {saveError ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 p-3 text-sm text-brand-red"
          data-testid="profile-save-error"
        >
          {saveError}
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  hint,
  children
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block space-y-1.5">
      <span className="block text-xs font-semibold uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      {children}
      {hint ? <span className="block text-xs text-zinc-500">{hint}</span> : null}
    </label>
  );
}

function Input({
  value,
  onChange,
  testid,
  type = "text",
  autoComplete
}: {
  value: string;
  onChange: (v: string) => void;
  testid: string;
  type?: string;
  autoComplete?: string;
}): JSX.Element {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      className="block min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-brand-black shadow-sm transition placeholder:text-zinc-400 focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
      data-testid={testid}
    />
  );
}
